'use client'

import React, { useState, useEffect } from 'react'
import { useTranslation } from 'react-i18next'
import { motion, AnimatePresence } from 'framer-motion'
import { Button } from '@/components/ui/button'
import { Label } from '@/components/ui/label'
import { Switch } from '@/components/ui/switch'
import { Input } from '@/components/ui/input'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue
} from '@/components/ui/select'
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger
} from '@/components/ui/dropdown-menu'
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog'
import {
  filterEmptyChatWidgetsForNexusAccess,
  isNexusEmptyChatWidgetId,
  setEmptyChatWidgetsState,
  getInstanceKey,
  type EmptyChatWidgetsState,
  type EmptyChatWidgetId,
  type EmptyChatWidgetSettings
} from '@/lib/chat-empty-widgets-state'
import {
  IconCloud,
  IconCurrencyDollar,
  IconDots,
  IconMessageCircle,
  IconMessage2,
  IconNews,
  IconNote,
  IconPin,
  IconPlug,
  IconSettings,
  IconStar,
  IconStorm,
  IconPlus,
  IconAiAgents,
  IconUsers,
  IconX,
  IconWorld
} from '@tabler/icons-react'

const WIDGET_IDS: EmptyChatWidgetId[] = [
  'recent_chats',
  'pinned_chats',
  'starred_messages',
  'recent_notes',
  'clock',
  'sticky',
  'weather',
  'finance',
  'news',
  'shortcuts',
  'sites',
  'nexus_agents',
  'nexus_crm',
  'nexus_integrations',
  'nexus_conversations',
  'nexus_notes'
]

const WIDGETS_WITH_SETTINGS: EmptyChatWidgetId[] = ['clock', 'weather', 'news', 'finance', 'sticky']

const STICKY_COLORS = ['#c4a574', '#5b7c99', '#a65d57', '#6b8e6b']
const NEWS_CATEGORY_OPTIONS = [
  { value: '__all__', label: 'Top stories' },
  { value: 'technology', label: 'Technology' },
  { value: 'world', label: 'World' },
  { value: 'business', label: 'Business' },
  { value: 'science', label: 'Science' },
  { value: 'health', label: 'Health' },
  { value: 'sports', label: 'Sports' },
  { value: 'entertainment', label: 'Entertainment' }
] as const

const WIDGET_LABEL_KEYS: Partial<Record<EmptyChatWidgetId, string>> = {
  shortcuts: 'chat.empty_chat.prompts',
  sites: 'chat.empty_chat.sites',
  nexus_agents: 'chat.empty_chat.nexus_agents',
  nexus_crm: 'chat.empty_chat.nexus_crm',
  nexus_integrations: 'chat.empty_chat.nexus_integrations',
  nexus_conversations: 'chat.empty_chat.nexus_conversations',
  nexus_notes: 'chat.empty_chat.nexus_notes'
}

const WIDGET_LABELS: Record<EmptyChatWidgetId, string> = {
  recent_chats: 'Recent Chats',
  pinned_chats: 'Pinned Chats',
  starred_messages: 'Starred Messages',
  recent_notes: 'Recent Notes',
  clock: 'Clock',
  sticky: 'Sticky',
  weather: 'Weather',
  finance: 'Finance',
  news: 'News',
  shortcuts: 'Prompts',
  sites: 'Sites',
  nexus_agents: 'Nexus: Agents',
  nexus_crm: 'Nexus: CRM',
  nexus_integrations: 'Nexus: Integrations',
  nexus_conversations: 'Nexus: Conversations',
  nexus_notes: 'Nexus: Notes'
}

const WIDGET_GRID_ICONS: Record<EmptyChatWidgetId, React.ReactNode> = {
  recent_chats: <IconMessageCircle size={18} />,
  pinned_chats: <IconPin size={18} />,
  starred_messages: <IconStar size={18} />,
  recent_notes: <IconNote size={18} />,
  clock: null,
  sticky: <IconNote size={18} />,
  weather: <IconCloud size={18} />,
  finance: <IconCurrencyDollar size={18} />,
  news: <IconNews size={18} />,
  shortcuts: <IconStorm size={18} />,
  sites: <IconWorld size={18} />,
  nexus_agents: <IconAiAgents size={18} />,
  nexus_crm: <IconUsers size={18} />,
  nexus_integrations: <IconPlug size={18} />,
  nexus_conversations: <IconMessage2 size={18} />,
  nexus_notes: <IconNote size={18} />
}

interface CustomizeModalProps {
  open: boolean
  onOpenChange: (open: boolean) => void
  state: EmptyChatWidgetsState
  onStateChange: (state: EmptyChatWidgetsState) => void
  hasNexusAccess?: boolean
}

export function WidgetSettingsDialog({
  widgetId,
  settings,
  onSave,
  open,
  onOpenChange
}: {
  widgetId: EmptyChatWidgetId
  settings: EmptyChatWidgetSettings
  onSave: (next: EmptyChatWidgetSettings) => void
  open: boolean
  onOpenChange: (open: boolean) => void
}) {
  const { t } = useTranslation()
  const [clockCity, setClockCity] = useState(settings.clockCity ?? '')
  const [weatherCity, setWeatherCity] = useState(settings.weatherCity ?? '')
  const [weatherUseCurrentLocation, setWeatherUseCurrentLocation] = useState(
    !!settings.weatherPreciseLocation
  )
  const [newsCategory, setNewsCategory] = useState(settings.newsCategory ?? '')
  const [financeTickersStr, setFinanceTickersStr] = useState(
    (settings.financeTickers ?? []).join(', ')
  )
  const [stickyColor, setStickyColor] = useState(settings.stickyColor ?? STICKY_COLORS[0])

  useEffect(() => {
    if (open) {
      setClockCity(settings.clockCity ?? '')
      setWeatherCity(settings.weatherCity ?? '')
      setWeatherUseCurrentLocation(!!settings.weatherPreciseLocation)
      setNewsCategory(settings.newsCategory ?? '')
      setFinanceTickersStr((settings.financeTickers ?? []).join(', '))
      setStickyColor(settings.stickyColor ?? STICKY_COLORS[0])
    }
  }, [open, settings])

  const handleSave = () => {
    const financeTickers = financeTickersStr
      .split(',')
      .map((s) => s.trim().toUpperCase())
      .filter(Boolean)
    onSave({
      ...settings,
      clockCity: widgetId === 'clock' ? clockCity.trim() || undefined : settings.clockCity,
      weatherCity: widgetId === 'weather' ? weatherCity.trim() || undefined : settings.weatherCity,
      weatherPreciseLocation:
        widgetId === 'weather' ? weatherUseCurrentLocation : settings.weatherPreciseLocation,
      newsCategory: widgetId === 'news' ? newsCategory.trim() || undefined : settings.newsCategory,
      financeTickers:
        widgetId === 'finance'
          ? financeTickers.length
            ? financeTickers
            : undefined
          : settings.financeTickers,
      stickyColor: widgetId === 'sticky' ? stickyColor : settings.stickyColor
    })
    onOpenChange(false)
  }

  if (widgetId === 'clock') {
    return (
      <Dialog open={open} onOpenChange={onOpenChange}>
        <DialogContent className="sm:max-w-sm">
          <DialogHeader>
            <DialogTitle>{t('chat.empty_chat.clock_settings', 'Clock settings')}</DialogTitle>
          </DialogHeader>
          <div className="space-y-2 pt-2">
            <Label htmlFor="clock-city" className="text-xs text-muted-foreground">
              Time city
            </Label>
            <Input
              id="clock-city"
              placeholder="e.g. King City"
              value={clockCity}
              onChange={(e) => setClockCity(e.target.value)}
            />
            <Button onClick={handleSave} className="w-full">
              {t('common.save', 'Save')}
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    )
  }
  if (widgetId === 'weather') {
    return (
      <Dialog open={open} onOpenChange={onOpenChange}>
        <DialogContent className="sm:max-w-sm">
          <DialogHeader>
            <DialogTitle>{t('chat.empty_chat.select_city', 'Select city')}</DialogTitle>
          </DialogHeader>
          <div className="space-y-3 pt-2">
            <div className="space-y-2">
              <Label htmlFor="weather-city" className="text-xs text-muted-foreground">
                City
              </Label>
              <Input
                id="weather-city"
                placeholder="e.g. Ottawa"
                value={weatherCity}
                onChange={(e) => setWeatherCity(e.target.value)}
              />
            </div>
            <div className="flex items-center gap-2">
              <Switch
                id="weather-use-location"
                checked={weatherUseCurrentLocation}
                onCheckedChange={setWeatherUseCurrentLocation}
              />
              <Label htmlFor="weather-use-location" className="text-xs text-muted-foreground">
                {t('chat.empty_chat.use_current_location', 'Use current location')}
              </Label>
            </div>
            <Button onClick={handleSave} className="w-full">
              {t('common.done', 'Done')}
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    )
  }
  if (widgetId === 'news') {
    return (
      <Dialog open={open} onOpenChange={onOpenChange}>
        <DialogContent className="sm:max-w-sm">
          <DialogHeader>
            <DialogTitle>{t('chat.empty_chat.news_settings', 'News settings')}</DialogTitle>
          </DialogHeader>
          <div className="space-y-2 pt-2">
            <Label htmlFor="news-category" className="text-xs text-muted-foreground">
              Category
            </Label>
            <Select
              value={newsCategory || '__all__'}
              onValueChange={(value) => setNewsCategory(value === '__all__' ? '' : value)}
            >
              <SelectTrigger id="news-category">
                <SelectValue placeholder="Select a category" />
              </SelectTrigger>
              <SelectContent>
                {NEWS_CATEGORY_OPTIONS.map((option) => (
                  <SelectItem key={option.value} value={option.value}>
                    {option.label}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
            <Button onClick={handleSave} className="w-full">
              {t('common.save', 'Save')}
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    )
  }
  if (widgetId === 'sticky') {
    return (
      <Dialog open={open} onOpenChange={onOpenChange}>
        <DialogContent className="sm:max-w-sm">
          <DialogHeader>
            <DialogTitle>{t('chat.empty_chat.sticky_color', 'Sticky color')}</DialogTitle>
          </DialogHeader>
          <div className="space-y-3 pt-2">
            <div className="flex flex-wrap gap-3">
              {STICKY_COLORS.map((color) => (
                <button
                  key={color}
                  type="button"
                  onClick={() => setStickyColor(color)}
                  className="size-10 rounded-full border-2 transition-all hover:opacity-90"
                  style={{
                    backgroundColor: color,
                    borderColor: stickyColor === color ? 'var(--primary)' : 'transparent',
                    boxShadow: stickyColor === color ? '0 0 0 2px var(--background)' : undefined
                  }}
                  aria-label={t('chat.empty_chat.select_color', 'Select color')}
                />
              ))}
            </div>
            <Button onClick={handleSave} className="w-full">
              {t('common.done', 'Done')}
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    )
  }
  if (widgetId === 'finance') {
    return (
      <Dialog open={open} onOpenChange={onOpenChange}>
        <DialogContent className="sm:max-w-sm">
          <DialogHeader>
            <DialogTitle>{t('chat.empty_chat.finance_settings', 'Finance settings')}</DialogTitle>
          </DialogHeader>
          <div className="space-y-2 pt-2">
            <Label htmlFor="finance-tickers" className="text-xs text-muted-foreground">
              Tickers (comma-separated)
            </Label>
            <Input
              id="finance-tickers"
              placeholder="e.g. AAPL, MSFT, GOOGL"
              value={financeTickersStr}
              onChange={(e) => setFinanceTickersStr(e.target.value)}
            />
            <Button onClick={handleSave} className="w-full">
              {t('common.save', 'Save')}
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    )
  }
  return null
}

function instanceSubtitle(id: EmptyChatWidgetId, settings: EmptyChatWidgetSettings): string {
  if (id === 'clock' && settings.clockCity) return settings.clockCity
  if (id === 'weather' && settings.weatherCity) return settings.weatherCity
  if (id === 'news' && settings.newsCategory) return settings.newsCategory
  if (id === 'finance' && settings.financeTickers?.length)
    return settings.financeTickers.slice(0, 2).join(', ')
  return ''
}

export function CustomizeModal({
  open,
  onOpenChange,
  state,
  onStateChange,
  hasNexusAccess = false
}: CustomizeModalProps) {
  const { t } = useTranslation()
  const [localState, setLocalState] = useState<EmptyChatWidgetsState>(
    filterEmptyChatWidgetsForNexusAccess(state, hasNexusAccess)
  )
  const [settingsModalInstanceKey, setSettingsModalInstanceKey] = useState<string | null>(null)
  const availableWidgetIds = WIDGET_IDS.filter(
    (id) => hasNexusAccess || !isNexusEmptyChatWidgetId(id)
  )

  useEffect(() => {
    if (open) {
      setLocalState(filterEmptyChatWidgetsForNexusAccess(state, hasNexusAccess))
    }
  }, [hasNexusAccess, open, state])

  const handleDone = () => {
    const safeState = filterEmptyChatWidgetsForNexusAccess(localState, hasNexusAccess)
    setEmptyChatWidgetsState(safeState)
    onStateChange(safeState)
    onOpenChange(false)
  }

  const toggleTrending = (v: boolean) => {
    setLocalState((s) => ({ ...s, showTrending: v }))
  }

  const addWidget = (id: EmptyChatWidgetId) => {
    if (!hasNexusAccess && isNexusEmptyChatWidgetId(id)) return

    const next: EmptyChatWidgetsState = {
      ...localState,
      enabledWidgets: [...localState.enabledWidgets, id]
    }
    setLocalState(next)
    setEmptyChatWidgetsState(next)
    onStateChange(next)
  }

  const removeWidgetAtIndex = (index: number) => {
    setLocalState((s) => ({
      ...s,
      enabledWidgets: s.enabledWidgets.filter((_, i) => i !== index)
    }))
  }

  const updateWidgetSettings = (instanceKey: string, next: EmptyChatWidgetSettings) => {
    setLocalState((s) => ({
      ...s,
      widgetSettingsMap: { ...(s.widgetSettingsMap ?? {}), [instanceKey]: next }
    }))
  }

  return (
    <>
      <AnimatePresence>
        {open && (
          <motion.div
            initial={{ height: 0, opacity: 0 }}
            animate={{ height: 'auto', opacity: 1 }}
            exit={{ height: 0, opacity: 0 }}
            transition={{ type: 'tween', duration: 0.25, ease: 'easeOut' }}
            className="overflow-hidden"
          >
            <div className="mt-4 flex max-h-[min(34rem,70vh)] flex-col gap-4 overflow-y-auto rounded-xl border border-border/60 bg-background/95 p-4 shadow-[0_20px_60px_-36px_rgba(15,23,42,0.55)] backdrop-blur">
              <div className="flex items-center justify-between">
                <h2 className="text-lg font-semibold text-foreground">
                  {t('chat.empty_chat.customize', 'Customize')}
                </h2>
                <div className="flex items-center gap-2">
                  <Button size="sm" onClick={handleDone}>
                    {t('common.done', 'Done')}
                  </Button>
                  <Button
                    variant="ghost"
                    size="icon"
                    className="size-8"
                    onClick={() => onOpenChange(false)}
                    aria-label={t('common.close', 'Close')}
                  >
                    <IconX size={18} />
                  </Button>
                </div>
              </div>

              <div className="flex items-center justify-between rounded-lg border border-border/60 bg-muted/20 px-3 py-2">
                <Label htmlFor="trending-toggle">
                  {t('chat.empty_chat.show_trending', 'Show Trending')}
                </Label>
                <Switch
                  id="trending-toggle"
                  checked={localState.showTrending}
                  onCheckedChange={toggleTrending}
                />
              </div>

              <div className="space-y-2">
                <Label className="text-xs text-muted-foreground">
                  {t('chat.empty_chat.your_widgets', 'Your widgets')}
                </Label>
                <p className="text-xs text-muted-foreground">
                  {t(
                    'chat.empty_chat.widgets_hint',
                    'Use ⋮ to remove or open settings. Add more below.'
                  )}
                </p>
                <div className="space-y-2">
                  {localState.enabledWidgets.map((id, index) => {
                    const instanceKey = getInstanceKey(localState.enabledWidgets, index)
                    const settings = localState.widgetSettingsMap?.[instanceKey] ?? {}
                    const hasSettings = WIDGETS_WITH_SETTINGS.includes(id)
                    const label = WIDGET_LABEL_KEYS[id]
                      ? t(WIDGET_LABEL_KEYS[id], WIDGET_LABELS[id])
                      : WIDGET_LABELS[id]
                    const subtitle = instanceSubtitle(id, settings)
                    return (
                      <div
                        key={instanceKey}
                        className="group flex items-center justify-between gap-2 rounded-lg border border-border/60 bg-card/80 px-3 py-2 transition-colors hover:bg-muted/30"
                      >
                        <div className="min-w-0 flex-1">
                          <span className="text-sm">{label}</span>
                          {subtitle ? (
                            <span className="ml-1.5 text-xs text-muted-foreground">
                              · {subtitle}
                            </span>
                          ) : null}
                        </div>
                        <div className="flex shrink-0 items-center gap-1">
                          {hasSettings && (
                            <button
                              type="button"
                              className="rounded-full p-1 text-muted-foreground opacity-100 transition-opacity hover:text-foreground sm:opacity-0 sm:group-hover:opacity-100"
                              onClick={() => setSettingsModalInstanceKey(instanceKey)}
                              aria-label={t('chat.empty_chat.widget_settings', 'Widget settings')}
                            >
                              <IconSettings size={16} />
                            </button>
                          )}
                          <DropdownMenu>
                            <DropdownMenuTrigger asChild>
                              <button
                                type="button"
                                className="flex size-8 items-center justify-center rounded-full border border-border/60 bg-background/90 text-muted-foreground opacity-100 transition-opacity hover:text-foreground sm:opacity-0 sm:group-hover:opacity-100"
                                aria-label={t('chat.empty_chat.remove_widget', 'Remove widget')}
                              >
                                <IconDots size={16} />
                              </button>
                            </DropdownMenuTrigger>
                            <DropdownMenuContent align="end">
                              <DropdownMenuItem
                                className="text-destructive focus:text-destructive"
                                onClick={() => removeWidgetAtIndex(index)}
                              >
                                {t('chat.empty_chat.remove', 'Remove')}
                              </DropdownMenuItem>
                            </DropdownMenuContent>
                          </DropdownMenu>
                        </div>
                      </div>
                    )
                  })}
                </div>
              </div>

              <div className="space-y-2">
                <Label className="text-xs text-muted-foreground">
                  {t('chat.empty_chat.add_widget', 'Add widget')}
                </Label>
                <div className="grid grid-cols-2 gap-2 sm:grid-cols-3 md:grid-cols-4">
                  {availableWidgetIds.map((id) => {
                    const label = WIDGET_LABEL_KEYS[id]
                      ? t(WIDGET_LABEL_KEYS[id], WIDGET_LABELS[id])
                      : WIDGET_LABELS[id]
                    const icon = WIDGET_GRID_ICONS[id]
                    return (
                      <button
                        key={id}
                        type="button"
                        onClick={() => addWidget(id)}
                        className="flex items-center gap-2 rounded-lg border border-border/60 bg-card/80 px-3 py-2.5 text-left transition-[transform,border-color,background-color] hover:-translate-y-0.5 hover:border-foreground/20 hover:bg-muted/40 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary/30 active:translate-y-0 active:scale-[0.99]"
                        aria-label={`Add ${label}`}
                      >
                        {icon != null ? (
                          <span className="shrink-0 text-muted-foreground">{icon}</span>
                        ) : null}
                        <span className="min-w-0 truncate text-sm text-foreground">{label}</span>
                        <span className="ml-auto shrink-0 rounded-full border border-primary/15 bg-primary/10 p-1">
                          <IconPlus size={14} className="text-primary" />
                        </span>
                      </button>
                    )
                  })}
                </div>
              </div>
            </div>
          </motion.div>
        )}
      </AnimatePresence>

      {settingsModalInstanceKey &&
        (() => {
          const widgetId = settingsModalInstanceKey.split('_')[0] as EmptyChatWidgetId
          if (!WIDGETS_WITH_SETTINGS.includes(widgetId)) return null
          const settings = localState.widgetSettingsMap?.[settingsModalInstanceKey] ?? {}
          return (
            <WidgetSettingsDialog
              widgetId={widgetId}
              settings={settings}
              onSave={(next) => updateWidgetSettings(settingsModalInstanceKey, next)}
              open={true}
              onOpenChange={(o) => !o && setSettingsModalInstanceKey(null)}
            />
          )
        })()}
    </>
  )
}
